<script>
import "@/assets/fonts/font-awesome-4.7.0/css/font-awesome.min.css";

export default {
  name: "MapActionPanel",
  props: {
    action: {
      type: Object,
      required: true
    },
    locations: {
      type: Array,
      required: true
    },
    activeId: {
      type: String,
      required: true
    },
    moving: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    panelStyle() {
      return {
        "--panel-accent": this.action.accent,
        "--panel-glow": this.action.glow
      };
    }
  },
  methods: {
    selectLocation(id) {
      this.$emit("select", id);
    },
    openLocation() {
      this.$emit("open");
    }
  }
};
</script>

<template>
  <section class="map-panel" :style="panelStyle" aria-live="polite">
    <div class="map-panel__handle" aria-hidden="true"></div>

    <div class="map-panel__location-nav" aria-label="ทางลัดพื้นที่">
      <button
        v-for="location in locations"
        :key="location.id"
        class="map-panel__map-dot"
        :class="{ 'map-panel__map-dot--active': activeId === location.id }"
        :style="{ '--dot-color': location.accent }"
        type="button"
        :aria-label="location.label"
        :aria-pressed="activeId === location.id ? 'true' : 'false'"
        @click="selectLocation(location.id)"
      >
        <i :class="location.icon" aria-hidden="true"></i>
      </button>
    </div>

    <div class="map-panel__content">
      <div class="map-panel__badge">
        <i :class="action.icon" aria-hidden="true"></i>
        <i aria-hidden="true"></i>
      </div>

      <div class="map-panel__copy">
        <small>
          <i aria-hidden="true"></i>
          {{ moving ? "กำลังเดินทาง" : "ถึงพื้นที่แล้ว" }}
        </small>
        <strong>{{ action.label }}</strong>
        <p>{{ action.caption }}</p>
      </div>

      <span class="map-panel__zone" aria-hidden="true">
        ZONE {{ action.zone }}
      </span>
    </div>

    <button
      class="map-panel__launch"
      type="button"
      :disabled="moving"
      @click="openLocation"
    >
      <i
        class="map-panel__launch-icon fa"
        :class="moving ? 'fa-spinner fa-spin' : 'fa-play'"
        aria-hidden="true"
      ></i>
      <span>
        <small>{{ moving ? "TRAVELLING" : "ENTER LOCATION" }}</small>
        <strong>
          {{ moving ? "กำลังเดินทาง..." : `เปิดเมนู ${action.label}` }}
        </strong>
      </span>
      <b aria-hidden="true">›</b>
    </button>
  </section>
</template>

<style scoped>
.map-panel {
  --panel-accent: #ff9b54;
  --panel-glow: rgba(255, 155, 84, 0.32);
  position: absolute;
  right: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom));
  left: 12px;
  z-index: 8;
  max-width: 620px;
  margin: 0 auto;
  padding: 8px 10px 10px;
  color: #f8fbff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  background: linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.14),
      transparent 36%
    ),
    rgba(13, 32, 50, 0.9);
  box-shadow: 0 18px 45px rgba(3, 20, 35, 0.42), 0 0 32px var(--panel-glow),
    inset 0 1px 0 rgba(255, 255, 255, 0.13);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.map-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background: radial-gradient(
    circle at 12% 42%,
    var(--panel-glow),
    transparent 36%
  );
  opacity: 0.8;
}

.map-panel__handle {
  width: 32px;
  height: 3px;
  margin: -2px auto 6px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.28);
}

.map-panel__location-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  gap: 9px;
}

.map-panel__map-dot {
  --dot-color: #ffffff;
  appearance: none;
  display: grid;
  place-items: center;
  width: 29px;
  height: 29px;
  padding: 0;
  color: rgba(255, 255, 255, 0.64);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(3, 16, 28, 0.55);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transform: translateY(0);
  transition: color 180ms ease, border-color 180ms ease, background 180ms ease,
    transform 180ms ease;
  -webkit-tap-highlight-color: transparent;
}

.map-panel__map-dot i {
  font-family: "FontAwesome";
  font-size: 10px;
  font-weight: 700;
}

.map-panel__map-dot--active {
  color: #ffffff;
  border-color: var(--dot-color);
  background: color-mix(in srgb, var(--dot-color) 28%, rgba(3, 16, 28, 0.8));
  box-shadow: 0 0 12px color-mix(in srgb, var(--dot-color) 45%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
  transform: translateY(-2px);
}

.map-panel__map-dot:focus-visible,
.map-panel__launch:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 3px;
}

.map-panel__content {
  position: relative;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  align-items: center;
  min-height: 48px;
  gap: 9px;
}

.map-panel__badge {
  position: relative;
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  color: #ffffff;
  border: 1px solid color-mix(in srgb, var(--panel-accent) 68%, transparent);
  border-radius: 14px;
  background: linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.17),
      transparent 55%
    ),
    color-mix(in srgb, var(--panel-accent) 32%, rgba(4, 18, 31, 0.88));
  box-shadow: 0 7px 17px rgba(0, 0, 0, 0.22), 0 0 18px var(--panel-glow),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
}

.map-panel__badge > i:first-child {
  font-family: "FontAwesome";
  font-size: 17px;
  font-weight: 700;
}

.map-panel__badge > i:last-child {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 9px;
  height: 9px;
  border: 2px solid #132b40;
  border-radius: 50%;
  background: #60e2a7;
  box-shadow: 0 0 7px #60e2a7;
}

.map-panel__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.map-panel__copy small {
  display: flex;
  align-items: center;
  color: color-mix(in srgb, var(--panel-accent) 75%, white);
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.map-panel__copy small i {
  width: 5px;
  height: 5px;
  margin-right: 5px;
  border-radius: 50%;
  background: var(--panel-accent);
  box-shadow: 0 0 7px var(--panel-accent);
}

.map-panel__copy strong {
  overflow: hidden;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.map-panel__copy p {
  overflow: hidden;
  margin: 0;
  color: rgba(230, 240, 247, 0.56);
  font-size: 10px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.map-panel__zone {
  position: absolute;
  top: 2px;
  right: 0;
  color: rgba(255, 255, 255, 0.25);
  font-family: "Poppins", sans-serif;
  font-size: 6px;
  font-weight: 700;
  letter-spacing: 0.15em;
}

.map-panel__launch {
  appearance: none;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 24px;
  align-items: center;
  width: 100%;
  min-height: 45px;
  margin-top: 6px;
  padding: 6px 11px;
  color: #102233;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 15px;
  background: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0.45),
      transparent 37%
    ),
    color-mix(in srgb, var(--panel-accent) 82%, #f8df94);
  box-shadow: 0 6px 0 color-mix(in srgb, var(--panel-accent) 52%, #273442),
    0 10px 20px rgba(0, 0, 0, 0.25), 0 0 20px var(--panel-glow),
    inset 0 1px 0 rgba(255, 255, 255, 0.52);
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
  -webkit-tap-highlight-color: transparent;
}

.map-panel__launch:active:not(:disabled) {
  transform: translateY(4px);
  box-shadow: 0 2px 0 color-mix(in srgb, var(--panel-accent) 52%, #273442),
    0 5px 12px rgba(0, 0, 0, 0.23), 0 0 14px var(--panel-glow);
}

.map-panel__launch:disabled {
  cursor: wait;
  filter: saturate(0.65) brightness(0.8);
}

.map-panel__launch-icon {
  font-family: "FontAwesome";
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.map-panel__launch > span:nth-child(2) {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.map-panel__launch small {
  font-family: "Poppins", sans-serif;
  font-size: 6px;
  font-weight: 700;
  letter-spacing: 0.16em;
  opacity: 0.55;
}

.map-panel__launch strong {
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.map-panel__launch b {
  display: grid;
  place-items: center;
  width: 23px;
  height: 23px;
  border: 1px solid rgba(16, 34, 51, 0.3);
  border-radius: 50%;
  font-family: "Poppins", sans-serif;
  font-size: 19px;
  line-height: 1;
}

@media (prefers-reduced-motion: reduce) {
  .map-panel__map-dot,
  .map-panel__launch {
    transition: none;
  }
}
</style>
